<script setup lang="ts">
import { ref } from 'vue';
import { useToast } from '@/composables/useToast';
import { useGlobalState } from '@/composables/useGlobalState';
import ConfirmModal from './Utils/ConfirmModal.vue';

const props = defineProps<{
    config: { icsUrl: string; activeProviders: string[]; } | null;
}>();

const emit = defineEmits<{
    (e: 'config-updated'): void
}>();

const { addToast } = useToast();
const { getAuthHeader } = useGlobalState();

const loading = ref(false);
const forms = ref({
    episod: { username: '', password: '' },
    sanctuary: { username: '', password: '' }
});

const showConfirmModal = ref(false);

const saveCredentials = async (provider: string, credentials: any) => {
    loading.value = true;
    try {
        const response = await fetch('/api/sport/credentials', {
            method: 'POST',
            headers: {
                ...getAuthHeader(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ provider, credentials })
        });

        if (response.ok) {
            addToast(`Identifiants ${provider} enregistrés`, 'success');
            emit('config-updated');
        } else {
            throw new Error();
        }
    } catch (e) {
        addToast(`Erreur lors de l'enregistrement des identifiants`, 'error');
    } finally {
        loading.value = false;
    }
};

const promptRegenerateToken = () => {
    showConfirmModal.value = true;
};

const executeRegenerateToken = async () => {
    showConfirmModal.value = false;
    loading.value = true;
    try {
        await fetch('/api/sport/calendar/token', {
            method: 'PATCH',
            headers: getAuthHeader()
        });
        addToast('Nouveau token généré avec succès', 'success');
        emit('config-updated');
    } catch (e) {
        addToast('Erreur lors de la régénération du token', 'error');
    } finally {
        loading.value = false;
    }
};

const copyToClipboard = async (text: string) => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            textArea.remove();
        }
        addToast('URL copiée dans le presse-papier', 'success');
    } catch (err) {
        addToast('Erreur lors de la copie', 'error');
    }
};
</script>

<template>
    <div v-if="config" class="row g-4">
        <!-- ICS Export Section -->
        <div class="col-12">
            <div class="card border-0 shadow-sm p-4 bg-dark text-white rounded-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                    <h2 class="h5 mb-0 fw-bold">Flux Calendrier ICS</h2>
                    <button @click="promptRegenerateToken" :disabled="loading" class="btn btn-outline-light btn-sm border-0 opacity-75 hover-opacity-100">
                        Régénérer le lien
                    </button>
                </div>
                <p class="text-secondary small">Utilisez cette URL pour synchroniser vos séances dans Google Calendar ou Apple Calendar.</p>
                <div class="d-flex gap-2">
                    <input type="text" readonly :value="config.icsUrl" class="form-control form-control-sm bg-secondary bg-opacity-10 border-0 text-white font-monospace">
                    <button @click="copyToClipboard(config.icsUrl)" class="btn btn-light btn-sm px-3">Copier</button>
                </div>
            </div>
        </div>

        <!-- Episod Credentials -->
        <div class="col-md-6">
            <div class="card border-0 shadow-sm p-4 rounded-4 h-100">
                <div class="d-flex align-items-center mb-4">
                    <div class="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                        <i class="bi bi-person-badge text-primary"></i>
                    </div>
                    <h2 class="h5 mb-0 fw-bold">Episod</h2>
                    <span v-if="config.activeProviders.includes('episod')" class="ms-auto badge bg-success-subtle text-success border border-success-subtle">Actif</span>
                </div>
                <div class="mb-3">
                    <label class="form-label small fw-bold text-secondary">Utilisateur</label>
                    <input v-model="forms.episod.username" type="text" class="form-control" placeholder="Email Episod">
                </div>
                <div class="mb-4">
                    <label class="form-label small fw-bold text-secondary">Mot de passe</label>
                    <input v-model="forms.episod.password" type="password" class="form-control" placeholder="••••••••">
                </div>
                <button @click="saveCredentials('episod', forms.episod)" :disabled="loading" class="btn btn-dark w-100 rounded-pill">
                    Enregistrer Episod
                </button>
            </div>
        </div>

        <!-- Sanctuary Credentials -->
        <div class="col-md-6">
            <div class="card border-0 shadow-sm p-4 rounded-4 h-100">
                <div class="d-flex align-items-center mb-4">
                    <div class="bg-dark bg-opacity-10 p-2 rounded-3 me-3">
                        <i class="bi bi-shield-lock text-dark"></i>
                    </div>
                    <h2 class="h5 mb-0 fw-bold">Sanctuary (Bsport)</h2>
                    <span v-if="config.activeProviders.includes('sanctuary')" class="ms-auto badge bg-success-subtle text-success border border-success-subtle">Actif</span>
                </div>
                <div class="mb-3">
                    <label class="form-label small fw-bold text-secondary">Utilisateur</label>
                    <input v-model="forms.sanctuary.username" type="text" class="form-control" placeholder="Email Sanctuary">
                </div>
                <div class="mb-4">
                    <label class="form-label small fw-bold text-secondary">Mot de passe</label>
                    <input v-model="forms.sanctuary.password" type="password" class="form-control" placeholder="••••••••">
                </div>
                <button @click="saveCredentials('sanctuary', forms.sanctuary)" :disabled="loading" class="btn btn-dark w-100 rounded-pill">
                    Enregistrer Sanctuary
                </button>
            </div>
        </div>
    </div>

    <div v-else class="text-center py-5">
        <div class="spinner-border text-dark" role="status"></div>
    </div>

    <ConfirmModal 
        :is-open="showConfirmModal"
        title="Régénérer le token"
        message="Êtes-vous sûr de vouloir régénérer votre token ? L'ancienne URL du calendrier ne fonctionnera plus."
        confirm-text="Régénérer"
        @close="showConfirmModal = false"
        @confirm="executeRegenerateToken"
    />
</template>

<style scoped>
.rounded-4 {
    border-radius: 1.25rem;
}
</style>
